/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Adicionado import do ícone
import api from "../api";
import safeLogger from "../utils/safeLogger";
//import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Chart } from "chart.js";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Gera nome de arquivo seguro a partir de base e data atual
const makeFilename = (base) => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yy = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  // display com barras para leitura humana, mas filename usa caracteres seguros
  const display = `${base} - ${dd}/${mm}/${yy} - ${hh}:${min}`;
  const filenameSafe = `${base} - ${dd}-${mm}-${yy} - ${hh}.${min}`;
  return { display, filenameSafe };
};

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [errorMessage, setErrorMessage] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [adminInfo, setAdminInfo] = useState({
    name: "Não informado",
    registration: "Não informado",
    company: "Não informado",
  });
  const [message, setMessage] = useState({ text: "", type: "" });

  // eslint-disable-next-line no-unused-vars
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => {
      setMessage({ text: "", type: "" });
    }, 3000); // 3 segundos
  };

  // Função para buscar o perfil do usuário
  const fetchUserProfile = async () => {
    try {
      const response = await api.get("/api/profile", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      safeLogger.info({ event: "profile_fetch", data: safeLogger.mask(response.data) });
      if (response.data) {
        const data = response.data || {};
        setAdminInfo({
          name: data.name || data.fullName || data.userName || data.displayName || "Não informado",
          registration:
            data.registration || data.matricula || data.registrationNumber || data.employeeId || data.registration_number || data.matriculaNumber || data.document || data.cpf || data.id || "Não informado",
          company: data.companyName || data.company || data.organization || "Não informado",
        });
      }
    } catch (error) {
      safeLogger.error("Erro ao buscar perfil do usuário:", error.message);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/admin/users", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      // Normaliza usuários: garante campos de email, name e registration (matrícula)
      const usersRaw = response.data || [];
      const normalized = usersRaw.map((u) => {
        const email = u.email || u.userEmail || (u.contact && u.contact.email) || "";
        const name = u.name || u.fullName || u.userName || (u.profile && u.profile.name) || "";
        const registration =
          u.registration || u.matricula || u.registrationNumber || u.employeeId || u.registration_number || (u.company && u.company.registration) || "";
        return {
          ...u,
          email,
          normalizedName: name,
          normalizedRegistration: registration,
        };
      });
      safeLogger.info("fetchUsers normalized:", safeLogger.mask(normalized));
      setUsers(normalized);
    } catch (error) {
      safeLogger.error("Erro ao carregar usuários:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
    fetchUsers();
  }, []);

  const handleToggleStatus = async (email, currentStatus) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/admin/users/findByEmail?email=${email}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const userId = response.data.id;
      const updatedStatus = currentStatus === "active" ? "inactive" : "active";
      await api.patch(
        `/api/admin/users/${userId}/status`,
        { status: updatedStatus },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      fetchUsers();
    } catch (error) {
      safeLogger.error("Erro ao atualizar status:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para selecionar ou desmarcar usuários
  const handleUserSelection = (userId) => {
    setSelectedUsers(
      (prev) =>
        prev.includes(userId)
          ? prev.filter((id) => id !== userId) // Remove se já estiver selecionado
          : [...prev, userId] // Adiciona se não estiver selecionado
    );
  };

  const handleExport = async () => {
    try {
      if (!exportFormat) {
        setMessage({
          text: "Selecione um formato para exportação.",
          type: "error",
        });
        return;
      }

      if (selectedUsers.length === 0) {
        setMessage({
          text: "Selecione ao menos um usuário para exportar.",
          type: "error",
        });
        return;
      }

      setLoading(true);

      // Pedimos blob/text, mas tentamos parsear JSON caso o backend retorne dados estruturados
      const response = await api.post(
        "/api/admin/users/export",
        { userIds: selectedUsers, exportType: exportFormat },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        }
      );

      safeLogger.info("Resposta do backend (blob):", safeLogger.mask(response));

      // Tentativa de ler blob como texto JSON
      let parsedData = null;
      try {
        const text = await response.data.text();
        parsedData = JSON.parse(text);
        safeLogger.info("Parsed admin export JSON:", safeLogger.mask(parsedData));
      } catch (e) {
        // não é JSON — fallback para baixar o blob como veio
        parsedData = null;
      }

      if (parsedData && parsedData.activities && parsedData.adminInfo) {
        // Enriquecer atividades com dados disponíveis no frontend (users)
        const enriched = enrichActivities(parsedData.activities);
        if (exportFormat === "pdf") {
          generatePDFWithChart(enriched, parsedData.adminInfo);
        } else if (exportFormat === "excel") {
          await generateExcelFromActivities(enriched, parsedData.adminInfo);
          setMessage({ text: "Relatório exportado com sucesso!", type: "success" });
          setTimeout(() => setMessage({ text: "", type: "" }), 3000);
        }
      } else {
        // Fallback: arquivo binário retornado pelo servidor
        if (exportFormat === "pdf") {
          // tenta baixar o blob direto
          const blob = new Blob([response.data], { type: "application/pdf" });
          const { filenameSafe } = makeFilename("relatorio_atividades");
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${filenameSafe}.pdf`;
          document.body.appendChild(link);
          link.click();
          setTimeout(() => document.body.removeChild(link), 1000);
        } else if (exportFormat === "excel") {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const { filenameSafe: excelName } = makeFilename("relatorio_atividades");
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `${excelName}.xlsx`;
          document.body.appendChild(link);
          link.click();
          setTimeout(() => document.body.removeChild(link), 1000);
          setMessage({ text: "Relatório exportado com sucesso!", type: "success" });
          setTimeout(() => setMessage({ text: "", type: "" }), 3000);
        }
      }
    } catch (error) {
      safeLogger.error("Erro ao exportar:", error.message);
      setMessage({
        text: "Erro ao exportar o relatório. Verifique e tente novamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePDFWithChart = async (activities, parsedAdminInfo) => {
    safeLogger.info("generatePDFWithChart called", { activities: safeLogger.mask(activities) });
    safeLogger.info("admin info (remote):", safeLogger.mask(parsedAdminInfo));
    safeLogger.info("admin info (state):", safeLogger.mask(adminInfo));

    // Mescla as informações locais (obtidas via /api/profile) com as informações
    // retornadas pelo endpoint de exportação para garantir matrícula quando possível.
    const effectiveAdminInfo = { ...(adminInfo || {}), ...(parsedAdminInfo || {}) };

    if (!effectiveAdminInfo || !effectiveAdminInfo.name) {
      safeLogger.error("Erro: adminInfo efetivo está indefinido ou incompleto:", safeLogger.mask(effectiveAdminInfo));
      return;
    }

    // Tentar enriquecer matrícula do admin se estiver ausente
    let adminRegistration = effectiveAdminInfo.registration || effectiveAdminInfo.matricula || "";
    if (!adminRegistration) {
      // procurar nos usuários carregados por email
      const adminEmail = (effectiveAdminInfo.email || effectiveAdminInfo.userEmail || "").toLowerCase();
      if (adminEmail) {
        const found = users.find((u) => (u.email || "").toLowerCase() === adminEmail);
        if (found) {
          adminRegistration = found.normalizedRegistration || found.registration || found.matricula || "";
        }
      }
      // último recurso: tentar buscar via endpoint
      if (!adminRegistration && (effectiveAdminInfo.email || effectiveAdminInfo.userEmail)) {
        try {
          const fetched = await fetchRegistrationForEmail(effectiveAdminInfo.email || effectiveAdminInfo.userEmail);
          if (fetched) adminRegistration = fetched;
        } catch (e) {
          safeLogger.warn("Falha ao buscar matrícula do admin via API:", e.message);
        }
      }
    }

    if (!adminRegistration) {
      safeLogger.warn("Matrícula do admin não encontrada; será exibido 'Não informado'.", safeLogger.mask(effectiveAdminInfo));
    }

    if (!activities || activities.length === 0) {
      safeLogger.error("Erro: Nenhuma atividade foi fornecida para gerar o PDF:", safeLogger.mask(activities));
      return;
    }

    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);

    // Cabeçalho
    doc.text("Relatório de Atividades por Usuário", 105, 10, {
      align: "center",
    });
    doc.setFontSize(12);
    doc.text(`Gerado por: ${adminInfo.name || "Não informado"}`, 10, 20);
    doc.text(`Matrícula: ${adminRegistration || "Não informado"}`, 10, 30);
    doc.text(`Empresa: ${adminInfo.company || "Não informado"}`, 10, 40);
    doc.text(
      `Data de Exportação: ${new Date().toLocaleString("pt-BR")}`,
      10,
      50
    );

    // Processar dados para o gráfico
    const activityTypes = activities.reduce((acc, activity) => {
      const type = activity.type || "Outros";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(activityTypes);
    const data = Object.values(activityTypes);

    // Gráfico de Uso
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 300;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");

    try {
      const chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [
            {
              label: "Atividades por Tipo",
              data: data,
              backgroundColor: [
                "#FF6384",
                "#36A2EB",
                "#FFCE56",
                "#4BC0C0",
                "#9966FF",
                "#FF9F40",
              ],
            },
          ],
        },
        options: {
          plugins: {
            legend: { position: "top" },
          },
          scales: {
            x: {
              ticks: {
                autoSkip: false, // Evita que os rótulos sejam pulados
                maxRotation: 0, // Rótulos na horizontal
                minRotation: 0, // Rótulos na horizontal
              },
            },
          },
        },
      });

      safeLogger.info("Gráfico criado:", safeLogger.mask(chart));

      await new Promise((resolve) => setTimeout(resolve, 500)); // Aguarda a renderização do gráfico
      const chartImage = canvas.toDataURL("image/png");
      doc.addImage(chartImage, "PNG", 50, 60, 110, 60); // Posição centralizada
    } finally {
      document.body.removeChild(canvas);
    }

    // Resumo Consolidado
    doc.setFontSize(14).text("Resumo Consolidado", 10, 130, { bold: true });
    let y = 140;
    Object.entries(activityTypes).forEach(([type, count]) => {
      const activityText = count === 1 ? "1 atividade" : `${count} atividades`;
      doc.setFontSize(12).text(`${type}: ${activityText}`, 10, y);
      y += 10;
    });

    // Tabela de Atividades
    autoTable(doc, {
      startY: y + 10,
      head: [["Usuário", "E-mail", "Data", "Tipo", "Detalhes"]],
      body: activities.map((activity) => [
        activity.userName || "Não informado",
        activity.userEmail || "Não informado",
        new Date(activity.date).toLocaleString("pt-BR"),
        activity.type || "Não informado",
        activity.details || "Não informado",
      ]),
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      styles: { fontSize: 10, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 20 },
        4: { cellWidth: 50 },
      },
    });

    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold"); // Negrito no rodapé
      doc.text(
        `Relatório gerado automaticamente pela CodeFort.`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 15,
        { align: "center" }
      );
      doc.text(
        `Todos os direitos reservados.`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    const { filenameSafe: pdfName } = makeFilename("relatorio_atividades");
    doc.save(`${pdfName}.pdf`);
  };

  // Enriquecer atividades com informações conhecidas (users carregados no frontend)
  const enrichActivities = (activities) => {
    return activities.map((act) => {
      const a = { ...act };
      const email = (a.userEmail || a.email || a.user?.email || "").toLowerCase();
      const user = users.find((u) => (u.email || "").toLowerCase() === email) || {};
      // prioriza campos já presentes na activity, depois usa usuário normalizado
      a.userName = a.userName || a.user?.name || user.normalizedName || user.name || "Não informado";
      a.userEmail = a.userEmail || a.email || user.email || a.user?.email || "Não informado";
      a.userRegistration =
        a.userRegistration || a.registration || a.matricula || user.normalizedRegistration || user.registration || user.matricula || "Não informado";
      return a;
    });
  };

  // Cache para resultados de busca por email
  const registrationCache = {};

  const fetchRegistrationForEmail = async (email) => {
    if (!email) return null;
    const key = email.toLowerCase();
    if (registrationCache[key]) return registrationCache[key];
    try {
      const resp = await api.get(`/api/admin/users/findByEmail?email=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const user = resp.data || {};
      const registration = user.registration || user.matricula || user.registrationNumber || user.employeeId || user.registration_number || "";
      registrationCache[key] = registration || null;
      return registrationCache[key];
    } catch (e) {
      safeLogger.warn("fetchRegistrationForEmail failed for", safeLogger.mask({ email }), e.message);
      registrationCache[key] = null;
      return null;
    }
  };

  // Versão assíncrona de enrichment que preenche matrícula consultando a API se necessário
  const enrichActivitiesAsync = async (activities) => {
    const out = [];
    for (const act of activities) {
      const a = { ...act };
      const email = (a.userEmail || a.email || a.user?.email || "").toLowerCase();
      const user = users.find((u) => (u.email || "").toLowerCase() === email) || {};
      a.userName = a.userName || a.user?.name || user.normalizedName || user.name || "Não informado";
      a.userEmail = a.userEmail || a.email || user.email || a.user?.email || "Não informado";
      let registration = a.userRegistration || a.registration || a.matricula || user.normalizedRegistration || user.registration || user.matricula || "";
      if (!registration) {
        // Tenta buscar via endpoint por email
        const fetched = await fetchRegistrationForEmail(a.userEmail || email);
        if (fetched) registration = fetched;
      }
      a.userRegistration = registration || "Não informado";
      out.push(a);
    }
    return out;
  };

  const generateExcelFromActivities = async (activities, adminInfo) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Atividades");

    worksheet.addRow(["Nome", "Matrícula", "E-mail", "Data", "Tipo", "Detalhes"]);
    activities.forEach((act) => {
      worksheet.addRow([
        act.userName || "Não informado",
        act.userRegistration || "Não informado",
        act.userEmail || "Não informado",
        act.date ? new Date(act.date).toLocaleString("pt-BR") : "",
        act.type || "",
        act.details || "",
      ]);
    });

    // Salvar
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const { filenameSafe } = makeFilename("relatorio_atividades");
    saveAs(blob, `${filenameSafe}.xlsx`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Painel de Gerenciamento do Admin
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando usuários...</Typography>
        </Box>
      ) : (
        <>
          <Typography sx={{ mb: 4 }}>
            Bem-vindo(a) ao painel de administração. Aqui você pode gerenciar os
            usuários vinculados à sua empresa.
          </Typography>
          {message.text && (
            <Box
              sx={{
                mt: 2,
                padding: "10px",
                backgroundColor:
                  message.type === "error" ? "#f8d7da" : "#d4edda",
                color: message.type === "error" ? "#721c24" : "#155724",
                border: "1px solid",
                borderColor: message.type === "error" ? "#f5c6cb" : "#c3e6cb",
                borderRadius: "4px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {message.text}
            </Box>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Selecionar</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        onChange={() => handleUserSelection(user.id)}
                        checked={selectedUsers.includes(user.id)}
                        disabled={user.status === "pendente"} // Desabilita se o status for "pendente"
                      />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            user.status === "active"
                              ? "green"
                              : user.status === "inactive"
                              ? "red"
                              : "gray",
                          fontWeight: "bold",
                        }}
                      >
                        {user.status === "active"
                          ? "Ativo"
                          : user.status === "inactive"
                          ? "Inativo"
                          : "Pendente"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user.status === "active"}
                        onChange={() =>
                          handleToggleStatus(user.email, user.status)
                        }
                        disabled={user.status === "pendente"}
                      />
                      <Tooltip title="Usuários inativos por mais de 30 dias serão excluídos.">
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 20,
                            marginLeft: 1,
                            color: "gray",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 4, display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl
              sx={{
                width: 200,
                marginTop: 2,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: "#1d4f91",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1d4f91",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1d4f91",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  padding: "0 4px",
                },
              }}
            >
              <InputLabel id="export-format-label">Formato</InputLabel>
              <Select
                labelId="export-format-label"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#000000",
                }}
              >
                <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExport}
                disabled={selectedUsers.length === 0}
              >
                Exportar Usuários Selecionados
              </Button>
              {errorMessage && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;
